import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  flowerStatDataAtom,
  flowerStatDateTypeAtom,
  flowerStatStatusAtom,
} from '@shared/api/flowerStat/flowerStat.atom';
import { useFlowerStatHook } from '@shared/api/flowerStat/flowerStat.hook';
import { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { Loading } from '@shared/components/Loading';
import { ReloadButton } from '@shared/components/ReloadButton';
import StatPageSideBar from '@shared/components/StatPageSideBar';
import SearchBar from '@shared/containers/SearchBar/SearchBar';
import { searchTextAtomFamily } from '@shared/containers/SearchBar/state/searchBar.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import dayjs from 'dayjs';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

import { AgencyAdminShopOrderListTable } from '../containers/AgencyAdminShopOrderListTable/AgencyAdminShopOrderListTable';
import AgencyAdminShopOrderListTableMobile from '../containers/AgencyAdminShopOrderListTable/AgencyAdminShopOrderListTableMobile';
import { useTitleHook } from '../hooks/useTitleHook';

const SEARCH_TEXT_ID = 'agencyAdminFlowerStatPage';

//* 기본 조회 기간: 최근 31일
const DEFAULT_START_DATE = dayjs().subtract(30, 'day').startOf('day').toISOString();
const DEFAULT_END_DATE = dayjs().endOf('day').toISOString();

const AgencyAdminFlowerStatPage: FC = function AgencyAdminFlowerStatPage() {
  useTitleHook('관리자용 페이지', undefined, undefined, true, '/');

  const location = useLocation();
  const showSettings = useMemo(
    () => new URLSearchParams(location.search).get('settings') === 'true',
    [location.search],
  );

  //* 날짜 필터 기준: 판매일(purchasedAt) 또는 발인일(coffinOutDate) — localStorage persist
  const [dateType, setDateType] = useRecoilState(flowerStatDateTypeAtom);
  // settings 모드가 아니면 dateType을 보내지 않음 (백엔드가 bugoBrand.accountDateType을 자동 적용)
  const effectiveDateType = showSettings ? dateType : undefined;

  //* 조회 날짜 범위 (테이블에서 변경 시 API 재호출)
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);

  const onDateRangeChange = useCallback((newStart: string, newEnd: string) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  }, []);

  //* 새로운 flower-stat hook 사용 (백엔드에서 flat 데이터 반환)
  useFlowerStatHook(startDate, endDate, effectiveDateType);

  const flowerStatStatus = useRecoilValue(flowerStatStatusAtom);
  const flowerStatData = useRecoilValue(flowerStatDataAtom);
  const searchText = useRecoilValue(searchTextAtomFamily(SEARCH_TEXT_ID));

  const { userProfile, bugoRole } = useAuth();

  //*mobile 기준
  const isMobile = !useMediaQuery({
    query: '(min-width: 1024px)',
  });

  const teamTypesFilter = useMemo(() => {
    if (bugoRole() === LowerRole.BugoAgencyBranchAdmin) {
      return null;
    } else {
      const bugoBrand = userProfile?.bugoAgencyAdminDetail?.bugoBrands[0];
      const teamTypes = bugoBrand?.bugoAgency?.teamTypes ?? [];
      const result: CheckBoxFilter = {};
      teamTypes.forEach((teamType) => (result[teamType] = true));

      return result;
    }
  }, [bugoRole, userProfile?.bugoAgencyAdminDetail?.bugoBrands]);

  //* 데이터가 존재하면 refetch 중에도 테이블 유지 (unmount 방지 → 로컬 상태 보존)
  const hasData = !!flowerStatData;
  const isRefetching = flowerStatStatus !== 'success';

  const table = useMemo(() => {
    if (hasData) {
      return (
        <div className="relative">
          {isRefetching && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
              <Loading />
            </div>
          )}
          <AgencyAdminShopOrderListTable
            flowerStatList={flowerStatData.data}
            teamTypesFilter={teamTypesFilter}
            dateType={effectiveDateType}
            onDateTypeChange={setDateType}
            onDateRangeChange={onDateRangeChange}
          />
        </div>
      );
    } else {
      return (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      );
    }
  }, [
    hasData,
    isRefetching,
    flowerStatData,
    teamTypesFilter,
    effectiveDateType,
    setDateType,
    onDateRangeChange,
  ]);

  if (isMobile) {
    return (
      <div>
        {/* Search Bar */}
        <div className="p-4">
          <SearchBar
            searchTextAtomId={SEARCH_TEXT_ID}
            placeholder="소속/지도사성함/지역/회원번호로 검색"
          />
        </div>
        {hasData ? (
          <div className="relative">
            {isRefetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <Loading />
              </div>
            )}
            <AgencyAdminShopOrderListTableMobile
              flowerStatList={flowerStatData.data}
              teamTypesFilter={teamTypesFilter}
              searchValue={searchText}
              dateType={effectiveDateType}
              onDateTypeChange={setDateType}
              onDateRangeChange={onDateRangeChange}
            />
          </div>
        ) : (
          <div className="center-box h-screen-15">
            <Loading />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="flex">
        {/* Sidebar */}
        <div className="flex flex-col">
          <StatPageSideBar />
        </div>
        <div className="mx-auto max-w-screen-2xl flex-1">
          {/* Table */}
          <div className="py-8 px-10">
            <div className="flex justify-between">
              <div>
                <p className="text-20 font-bold">화환 내역 조회</p>
                <p className="font-sm pt-2 text-gray-500">
                  조건을 설정하여 조회할 수 있습니다. (최대 60일 기간 데이터를 조회가능)
                </p>
              </div>
              <ReloadButton />
            </div>
            <div className="pt-4">{table}</div>
          </div>
        </div>
      </div>
    );
  }
};

export default AgencyAdminFlowerStatPage;
